import React, { useState } from 'react';
import { ArrowRight, Shield, CheckCircle, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

const countryCodes = [
  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}' }, { code: '+44', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+91', flag: '\u{1F1EE}\u{1F1F3}' }, { code: '+86', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '+81', flag: '\u{1F1EF}\u{1F1F5}' }, { code: '+49', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: '+33', flag: '\u{1F1EB}\u{1F1F7}' }, { code: '+61', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: '+55', flag: '\u{1F1E7}\u{1F1F7}' }, { code: '+52', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: '+82', flag: '\u{1F1F0}\u{1F1F7}' }, { code: '+971', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+65', flag: '\u{1F1F8}\u{1F1EC}' }, { code: '+64', flag: '\u{1F1F3}\u{1F1FF}' },
];

const HeroBanner = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const handlePhoneChange = (v) => {
    const d = v.replace(/\D/g, '');
    if (d.length <= 10) { setFormData({ ...formData, phone: d }); setErrors({ ...errors, phone: d && d.length !== 10 ? 'Phone must be 10 digits' : '' }); }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const ne = { email: '', phone: '' }; let has = false;
    if (!validateEmail(formData.email)) { ne.email = 'Enter a valid email'; has = true; }
    if (formData.phone.length !== 10) { ne.phone = 'Phone must be 10 digits'; has = true; }
    if (has) { setErrors(ne); return; }
    toast({ title: "Request Received!", description: "Our trademark experts will contact you within 24 hours." });
    setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' }); setErrors({ email: '', phone: '' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 pb-16 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0D0507 0%, #1A0A0E 40%, #2A1118 100%)' }}>
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(123,35,50,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(201,168,92,0.15) 0%, transparent 40%)' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3 space-y-8">
            <div className="inline-flex items-center gap-2 bg-wine-800/50 border border-wine-600/30 px-4 py-2 rounded-full">
              <span className="w-2 h-2 bg-gold-400 rounded-full animate-pulse"></span>
              <span className="text-xs font-medium text-wine-200 uppercase tracking-wider">US Trademark Registration | Online Filing</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
              <span className="text-white">Protect Your Brand.</span><br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-500">Register Your Trademark.</span>
            </h1>

            <p className="text-lg text-wine-200 leading-relaxed max-w-xl">
              Professional trademark registration with expert USPTO filing, comprehensive search, and federal brand protection. Starting at <span className="text-gold-400 font-semibold">$45</span>.
            </p>

            <div className="flex flex-wrap gap-6 text-sm text-wine-300">
              <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-gold-400" /> Federal Protection</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-gold-400" /> USPTO Filing</span>
              <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-gold-400" /> Expert Attorneys</span>
            </div>
          </div>

          <div data-testid="hero-contact-form" className="lg:col-span-2 bg-white rounded-2xl p-7 shadow-2xl shadow-wine-950/80">
            <div className="text-center mb-5">
              <span className="inline-block bg-wine-600 text-white px-4 py-1.5 rounded-full text-xs font-bold mb-3">Limited Offer: $45 Only</span>
              <h3 className="text-xl font-bold text-wine-950 mb-1">Start Your Trademark Filing</h3>
              <p className="text-sm text-stone-500">Get expert guidance and protect your brand today.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="hero-name" className="text-stone-700 text-sm font-medium">Name *</Label>
                <Input id="hero-name" data-testid="hero-name-input" placeholder="Enter your full name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="h-10 border-stone-300 focus:border-wine-500 focus:ring-wine-500/20" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="hero-email" className="text-stone-700 text-sm font-medium">Email *</Label>
                <Input id="hero-email" data-testid="hero-email-input" type="email" placeholder="you@company.com" value={formData.email} onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setErrors({ ...errors, email: '' }); }} required className={`h-10 ${errors.email ? 'border-red-400' : 'border-stone-300'} focus:border-wine-500`} />
                {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
              </div>
              <div className="space-y-1">
                <Label className="text-stone-700 text-sm font-medium">Contact Number *</Label>
                <div className="flex gap-2">
                  <Select value={formData.countryCode} onValueChange={(v) => setFormData({ ...formData, countryCode: v })}>
                    <SelectTrigger data-testid="hero-country-code" className="w-[100px] h-10 border-stone-300"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {countryCodes.map((c) => (<SelectItem key={c.code} value={c.code}><span className="flex items-center gap-1">{c.flag} {c.code}</span></SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Input data-testid="hero-phone-input" type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 ${errors.phone ? 'border-red-400' : 'border-stone-300'} focus:border-wine-500`} />
                </div>
                {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="hero-desc" className="text-stone-700 text-sm font-medium">Description</Label>
                <Textarea id="hero-desc" data-testid="hero-description-input" placeholder="What would you like to protect?" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="border-stone-300 focus:border-wine-500 resize-none" />
              </div>
              <Button data-testid="hero-submit-btn" type="submit" className="w-full bg-wine-600 hover:bg-wine-700 text-white h-10 text-sm font-semibold transition-all">
                Register My Trademark Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <p className="text-xs text-stone-400 text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
